import moment from 'moment'
import type { TJumpTarget } from 'components/DateSeparator'

/**
 * Day grouping for any dated list, shared by the room chat's message dividers
 * and the admin image library.
 *
 * Grouping is contiguous rather than by lookup, so it preserves whatever order
 * the caller supplies: the chat reads oldest-first, the image library
 * newest-first, and both produce the same day boundaries.
 */

export interface DayGroup<T> {
	/** Stable key: the day's ISO date. */
	key: string
	/** "Monday, August 3rd", with the year added when it is not this one. */
	label: string
	items: T[]
}

/**
 * The label the divider pill shows.
 *
 * Spelled out in full rather than as "Yesterday": these lists are read as a
 * record, where a relative label is less useful than a date, and it stays
 * correct however long the page stays open.
 */
export const dayGroupLabel = (date: string | Date, now: moment.Moment = moment()): string => {
	const at = moment(date)
	return at.year() === now.year()
		? at.format('dddd, MMMM Do')
		: at.format('dddd, MMMM Do, YYYY')
}

/**
 * @param items  the list, already in the order it will be displayed
 * @param getDate reads the timestamp a given item is grouped by
 */
export const groupByDay = <T>(
	items: T[],
	getDate: (item: T) => string | Date | undefined | null,
): DayGroup<T>[] => {
	const days: DayGroup<T>[] = []
	const now = moment()

	;(items || []).forEach(item => {
		const raw = getDate(item)
		// An item with no usable date would otherwise open a group labelled
		// "Invalid date"; park it in the current run instead.
		const at = raw ? moment(raw) : null
		if (!at || !at.isValid()) {
			if (days.length > 0) days[days.length - 1].items.push(item)
			return
		}

		const key = at.format('YYYY-MM-DD')
		const last = days[days.length - 1]

		if (last && last.key === key) {
			last.items.push(item)
			return
		}

		days.push({ key, label: dayGroupLabel(at.toDate(), now), items: [item] })
	})

	return days
}

/**
 * The day a jump should land on, for a list ordered NEWEST FIRST.
 *
 * The room chat reads oldest-first and resolves this inline; the image library
 * reads newest-first, which flips what "recent" and "beginning" mean and which
 * end of the array a date window resolves to. Kept pure so both the direction
 * and the boundaries can be asserted without a DOM.
 *
 * @param dayKeys ISO day keys, newest first
 */
export const resolveJumpDayKey = (
	dayKeys: string[],
	target: Exclude<TJumpTarget, 'pickDate'>,
	now: moment.Moment = moment(),
): string | undefined => {
	if (!dayKeys || dayKeys.length === 0) return undefined

	if (target === 'recent') return dayKeys[0]
	if (target === 'beginning') return dayKeys[dayKeys.length - 1]

	const from = target === 'lastWeek' ? now.clone().subtract(7, 'days') : now.clone().subtract(1, 'month')
	return earliestDayOnOrAfter(dayKeys, from)
}

/**
 * The earliest day that is still on or after `from` — the far edge of the
 * window, so jumping to "Last Week" lands on the oldest day inside it and
 * scrolling onward continues into the past.
 *
 * In a newest-first array that is the LAST entry satisfying the test. Falls
 * back to the newest day when nothing is recent enough, which is the closest
 * the list can get to what was asked for.
 */
export const earliestDayOnOrAfter = (
	dayKeys: string[],
	from: moment.Moment,
): string | undefined => {
	if (!dayKeys || dayKeys.length === 0) return undefined

	for (let i = dayKeys.length - 1; i >= 0; i -= 1) {
		if (!moment(dayKeys[i], 'YYYY-MM-DD').isBefore(from, 'day')) return dayKeys[i]
	}

	return dayKeys[0]
}

export default groupByDay
